import { useSyncExternalStore } from "react";
import * as Sentry from "@sentry/react";

/**
 * Cookie/analytics consent. The app sets only strictly-necessary cookies (the
 * session). Session Replay is the one optional, consent-gated thing: it
 * records DOM snapshots on errors, which is personal data under GDPR.
 */
export type Consent = "unset" | "all" | "necessary";
const KEY = "consent.v1";
const listeners = new Set<() => void>();
let replayEnabled = false;

export function readConsent(): Consent {
  try {
    const v = localStorage.getItem(KEY);
    return v === "all" || v === "necessary" ? v : "unset";
  } catch {
    return "unset";
  }
}

export function setConsent(value: Exclude<Consent, "unset">): void {
  try {
    localStorage.setItem(KEY, value);
  } catch {
    /* private mode */
  }
  applyConsent(value);
  listeners.forEach((l) => l());
}

export function applyConsent(value: Consent = readConsent()): void {
  if (value === "all" && !replayEnabled) {
    Sentry.addIntegration(Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }));
    replayEnabled = true;
  } else if (value !== "all" && replayEnabled) {
    // Consent withdrawn (or narrowed): stop recording immediately.
    void Sentry.getReplay()?.stop();
    replayEnabled = false;
  }
}

export function useConsent(): Consent {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    readConsent,
    () => "unset" as const,
  );
}
